import { useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, ClipboardList, UtensilsCrossed, Sparkles, ChefHat, Wine } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceTypes = [
  { value: "buffet", label: "Buffet", icon: UtensilsCrossed, description: "Self-service buffet style" },
  { value: "gala_dinner", label: "Gala Dinner", icon: Sparkles, description: "Formal seated service" },
];

const roleLabels = {
  // Front of House
  event_manager: "Event Manager",
  reception: "Reception",
  // Buffet Catering
  kitchen_chef: "Kitchen/Chef",
  buffet_servers: "Buffet Servers",
  hospitality_bussers: "Hospitality/Bussers",
  bartenders: "Bartenders",
  // Gala Dinner Catering
  banquet_manager: "Banquet Manager",
  lead_server: "Lead Server",
  servers: "Servers",
};

const roleIcons = {
  event_manager: Users,
  reception: ClipboardList,
  kitchen_chef: ChefHat,
  buffet_servers: UtensilsCrossed,
  hospitality_bussers: Users,
  bartenders: Wine,
  banquet_manager: Users,
  lead_server: Users,
  servers: UtensilsCrossed,
};

export const StaffingPlanner = () => {
  // Front of House state
  const [fohGuestCount, setFohGuestCount] = useState("");
  const [fohResult, setFohResult] = useState(null);
  const [fohLoading, setFohLoading] = useState(false);

  // Catering state
  const [cateringGuestCount, setCateringGuestCount] = useState("");
  const [serviceType, setServiceType] = useState("buffet");
  const [cateringResult, setCateringResult] = useState(null);
  const [cateringLoading, setCateringLoading] = useState(false);

  const handleFohCalculate = async () => {
    if (!fohGuestCount) {
      toast.error("Please enter guest count");
      return;
    }

    setFohLoading(true);
    try {
      const response = await axios.post(`${API}/calculate/staffing/front-of-house?guest_count=${parseInt(fohGuestCount)}`);
      setFohResult(response.data);
      toast.success("Front of house staffing calculated");
    } catch (error) {
      toast.error("Calculation failed");
      console.error(error);
    } finally {
      setFohLoading(false);
    }
  };

  const handleCateringCalculate = async () => {
    if (!cateringGuestCount) {
      toast.error("Please enter guest count");
      return;
    }

    setCateringLoading(true);
    try {
      const response = await axios.post(`${API}/calculate/staffing/catering`, {
        guest_count: parseInt(cateringGuestCount),
        service_type: serviceType
      });
      setCateringResult(response.data);
      toast.success("Catering staffing calculated");
    } catch (error) {
      toast.error("Calculation failed");
      console.error(error);
    } finally {
      setCateringLoading(false);
    }
  };

  const renderStaffCard = (role, count) => {
    const Icon = roleIcons[role] || Users;
    const label = roleLabels[role] || role;
    
    return (
      <div key={role} className="p-4 bg-secondary/30 rounded-sm" data-testid={`staff-${role}`}>
        <div className="flex items-center gap-2 mb-2">
          <div className="p-2 bg-cyan-500/20 rounded-sm">
            <Icon className="h-4 w-4 text-cyan-400" />
          </div>
          <span className="text-sm font-medium">{label}</span>
        </div>
        <p className="text-3xl font-mono font-bold text-cyan-400">{count}</p>
      </div>
    );
  };

  return (
    <Layout 
      title="Staffing Planner" 
      subtitle="Calculate staff requirements based on guest count and service type"
    >
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Front of House / Management Section */}
        <div className="space-y-4">
          <Card className="tactical-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg uppercase">
                <ClipboardList className="h-5 w-5 text-cyan-400" />
                Front of House / Management
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4 items-end">
                <div className="flex-1 space-y-2">
                  <Label htmlFor="foh-guests" className="label-tactical">Guest Count</Label>
                  <Input
                    id="foh-guests"
                    type="number"
                    placeholder="100"
                    value={fohGuestCount}
                    onChange={(e) => setFohGuestCount(e.target.value)}
                    className="tactical-input"
                    data-testid="foh-guest-count-input"
                  />
                </div>
                <Button 
                  onClick={handleFohCalculate} 
                  className="tactical-btn bg-cyan-600 hover:bg-cyan-700"
                  disabled={fohLoading}
                  data-testid="foh-calculate-button"
                >
                  {fohLoading ? "Calculating..." : "Calculate"}
                </Button>
              </div>

              {fohResult && (
                <div className="space-y-4 pt-4 border-t border-border/50">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      For {fohResult.guest_count} guests
                    </span>
                    <span className="text-sm font-mono">
                      Total: <span className="text-cyan-400 font-bold">{fohResult.total}</span>
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(fohResult.staff).map(([role, count]) => 
                      renderStaffCard(role, count)
                    )}
                  </div>
                  <div className="p-3 bg-secondary/20 rounded-sm">
                    <p className="text-xs text-muted-foreground">
                      <span className="font-semibold">Ratios:</span> Event Manager (1 per 100) • Reception (1 per 50)
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Catering / Bar Section */}
        <div className="space-y-4">
          <Card className="tactical-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg uppercase">
                <UtensilsCrossed className="h-5 w-5 text-cyan-400" />
                Catering / Bar
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div className="space-y-2">
                  <Label htmlFor="catering-guests" className="label-tactical">Guest Count</Label>
                  <Input
                    id="catering-guests"
                    type="number"
                    placeholder="100"
                    value={cateringGuestCount}
                    onChange={(e) => setCateringGuestCount(e.target.value)}
                    className="tactical-input"
                    data-testid="catering-guest-count-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-tactical">Service Type</Label>
                  <Select value={serviceType} onValueChange={setServiceType}>
                    <SelectTrigger className="tactical-input" data-testid="service-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className="h-4 w-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  onClick={handleCateringCalculate} 
                  className="tactical-btn bg-cyan-600 hover:bg-cyan-700"
                  disabled={cateringLoading}
                  data-testid="catering-calculate-button"
                >
                  {cateringLoading ? "Calculating..." : "Calculate"}
                </Button>
              </div>

              {/* Service Type Guide */}
              <div className="grid grid-cols-2 gap-3">
                {serviceTypes.map((type) => (
                  <div 
                    key={type.value}
                    className={`p-3 rounded-sm border transition-colors cursor-pointer ${
                      serviceType === type.value 
                        ? 'border-cyan-500/50 bg-cyan-500/10' 
                        : 'border-border/50 hover:border-border'
                    }`}
                    onClick={() => setServiceType(type.value)}
                  >
                    <div className="flex items-center gap-2">
                      <type.icon className={`h-5 w-5 ${serviceType === type.value ? 'text-cyan-400' : 'text-muted-foreground'}`} />
                      <p className={`text-sm font-semibold ${serviceType === type.value ? 'text-cyan-400' : ''}`}>
                        {type.label}
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{type.description}</p>
                  </div>
                ))}
              </div>

              {cateringResult && (
                <div className="space-y-4 pt-4 border-t border-border/50">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      For {cateringResult.guest_count} guests ({cateringResult.service_type.replace('_', ' ')})
                    </span>
                    <span className="text-sm font-mono">
                      Total: <span className="text-cyan-400 font-bold">{cateringResult.total}</span>
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(cateringResult.staff).map(([role, count]) => 
                      renderStaffCard(role, count)
                    )}
                  </div>
                  <div className="p-3 bg-secondary/20 rounded-sm">
                    <p className="text-xs text-muted-foreground">
                      <span className="font-semibold">Ratios:</span>{' '}
                      {serviceType === "buffet" ? (
                        <>Kitchen/Chef (1 per 50) • Buffet Servers (1 per 50) • Hospitality/Bussers (2 per 50) • Bartenders (2 per 50)</>
                      ) : (
                        <>Banquet Manager (1) • Lead Server (1) • Servers (1 per 10) • Bartenders (1 per 50)</>
                      )}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default StaffingPlanner;
